/**
 * useWssCheckIn Hook
 * Handles WSS check-in validation and submission logic
 * Constructs complete API payload before sending to service
 *
 * Production flow (2-step audit):
 *  1. All users → check-in first (POST) → get recordId
 *  2. SH3 + auditEligible → audit popup → feedback form → updateCheckIn
 *  3. HO Auditor / SH5 + auditEligible → audit popup → updateCheckIn
 *  4. Others → success directly
 */

import { useCallback, useMemo } from "react";
import { myWssService } from "../services/wssService";
import { calculateAccurateRange } from "../utils/gpsUtils";
import {
  validateLocationEnabled,
  validateRequiredFields,
} from "../utils/wssValidators";
import {
  RECORD_TYPE_NAME,
  ERROR_MESSAGES,
  WSS_PLACEHOLDER,
} from "../utils/wssConstants";

export const useWssCheckIn = ({
  wss,
  customerCode,
  distanceToWss,
  currentAccuracy,
  accuracy,
  territoryCheckIn,
  currentLatitude,
  currentLongitude,
  wssLatitude,
  wssLongitude,
  wssHasCoordinates,
  mtpCheckIn,
  userType,
  auditPopupData,
  setLoading,
  showSuccess,
  showError,
  setIsSubmitModalOpen,
  setIsAuditPopupOpen,
  setCheckinPayLoad,
  setGetSummeryList,
  selectedTerritory,
  // --- New params for 2-step audit flow ---
  isAuditEligible,
  recordId,
  setRecordId,
  setSelectLatLong,
  setAuditPopupData,
  setIsQuaterlyFlag,
}) => {
  /**
   * Calculate check-in validity based on distance
   */
  const isValidCheckIn = useMemo(() => {
    const calculatedRange = calculateAccurateRange(accuracy, currentAccuracy);

    return distanceToWss <= calculatedRange || !wssHasCoordinates
      ? "Valid"
      : "Invalid";
  }, [distanceToWss, accuracy, currentAccuracy, wssHasCoordinates]);

  /**
   * Prepare check-in payload
   */
  const prepareCheckInPayload = useCallback(
    (isCorrectCheckIn = "") => {
      // When user confirms location via recapture ("Yes"), force Valid — matches old MyWss.js behaviour
      // where prepareCheckInPayload hardcoded IsValidCheckIn: "Valid" for the Correct path
      const effectiveIsValid = isCorrectCheckIn === "Correct" ? "Valid" : isValidCheckIn;
      return {
        RecordtypeName: RECORD_TYPE_NAME,
        territory: territoryCheckIn,
        dealer: "",
        dealercode: "",
        customer: wss,
        customercode: customerCode,
        Transaction_lat: currentLatitude,
        Transaction_long: currentLongitude,
        IsValidCheckIn: effectiveIsValid,
        Master_lat: wssLatitude,
        Master_long: wssLongitude,
        Trasanction_distance: distanceToWss,
        Master_Accuracy: accuracy,
        Current_Accuracy: currentAccuracy,
        Is_Correct_CheckIn: isCorrectCheckIn,
        Retail_counter_present: auditPopupData?.retailCounter || "",
        non_performing_status: auditPopupData?.nonPerforming || "",
        Company_Name: auditPopupData?.nonPerformingCompany || "",
        Dealer_Check_in: "",
        fileBody: "",
        fileName: "",
      };
    },
    [
      territoryCheckIn,
      wss,
      customerCode,
      currentLatitude,
      currentLongitude,
      isValidCheckIn,
      wssLatitude,
      wssLongitude,
      distanceToWss,
      accuracy,
      currentAccuracy,
      auditPopupData,
    ]
  );

  /**
   * Perform check-in (POST) and handle the 2-step audit flow.
   * On success, stores recordId. If the user is SH3/SH5/HO Auditor
   * and audit-eligible, opens the audit popup instead of showing success.
   */
  const performCheckIn = useCallback(
    async (isCorrectCheckIn = "") => {
      try {
        setLoading(true);

        const payload = prepareCheckInPayload(isCorrectCheckIn);
        // Use the same effective validity that was put in the payload
        const effectiveIsValid = isCorrectCheckIn === "Correct" ? "Valid" : isValidCheckIn;
        const response = await myWssService.checkInWss(payload);

        setLoading(false);

        if (response.success) {
          // Store the new record ID for subsequent updateCheckIn calls
          const newRecordId = response.data?.id || "";
          setRecordId(newRecordId);

          // Close the recapture modal first
          setIsSubmitModalOpen(false);

          if (effectiveIsValid === "Invalid") {
            showError(ERROR_MESSAGES.CHECK_IN_UNSUCCESSFUL);
          } else if (
            (userType === "SH3" ||
              userType === "HO Auditor" ||
              userType === "SH5") &&
            isAuditEligible
          ) {
            // Open audit popup for eligible users
            setIsAuditPopupOpen(true);
          } else {
            setGetSummeryList(selectedTerritory);
            showSuccess(
              `Successfully checked-in for ${wss ? wss : "this"} wss!`
            );
          }
        } else {
          setIsSubmitModalOpen(false);
          showError(ERROR_MESSAGES.ALREADY_CHECKED_IN);
        }
      } catch (error) {
        console.error("Error during check-in:", error);
        showError(error.message || ERROR_MESSAGES.CHECK_IN_UNSUCCESSFUL);
        setLoading(false);
        setIsSubmitModalOpen(false);
      }
    },
    [
      prepareCheckInPayload,
      isValidCheckIn,
      wss,
      userType,
      isAuditEligible,
      selectedTerritory,
      setLoading,
      setRecordId,
      showSuccess,
      showError,
      setIsSubmitModalOpen,
      setIsAuditPopupOpen,
      setGetSummeryList,
    ]
  );

  /**
   * Handle direct check-in (within accurate range).
   * Production simplified: always calls performCheckIn for all users.
   */
  const handleDirectCheckIn = useCallback(
    (isCorrectCheckIn = "") => {
      performCheckIn(isCorrectCheckIn);
    },
    [performCheckIn]
  );

  /**
   * Handle normal check-in flow.
   * For SH3 + audit-eligible: directly calls performCheckIn (skip modal).
   * For others: same MTP / distance logic as before.
   */
  const handleNormalCheckin = useCallback(() => {
    const cal = 2 * (accuracy + currentAccuracy) + 300;

    if (mtpCheckIn === "Correct") {
      if (distanceToWss <= cal) {
        handleDirectCheckIn("");
      } else {
        performCheckIn("");
      }
    } else if (!wssHasCoordinates) {
      // No GPS coordinates registered — always show recapture modal so user can capture location
      setIsSubmitModalOpen(true);
    } else {
      if (distanceToWss <= cal) {
        handleDirectCheckIn("");
      } else {
        // Out of range — always show recapture modal for all user types
        setIsSubmitModalOpen(true);
      }
    }
  }, [
    mtpCheckIn,
    distanceToWss,
    accuracy,
    currentAccuracy,
    wssHasCoordinates,
    handleDirectCheckIn,
    performCheckIn,
    setIsSubmitModalOpen,
  ]);

  /**
   * Handle check-in button click.
   * Production: all users → handleNormalCheckin (no pre-audit popup).
   */
  const handleCheckInClick = useCallback(() => {
    // Validation 1: Location enabled
    const locationValidation = validateLocationEnabled(
      currentLatitude,
      currentLongitude
    );
    if (!locationValidation.isValid) {
      showError(locationValidation.error);
      return;
    }

    // Validation 2: Required fields
    const fieldValidation = validateRequiredFields(wss, territoryCheckIn);
    if (!fieldValidation.isValid) {
      showError(fieldValidation.error);
      return;
    }

    // All users go through normal check-in first
    handleNormalCheckin();
  }, [
    currentLatitude,
    currentLongitude,
    wss,
    territoryCheckIn,
    showError,
    handleNormalCheckin,
  ]);

  /**
   * Handle modal confirmation (re-capture yes)
   */
  const handleModalYesClick = useCallback(() => {
    performCheckIn("Correct");
  }, [performCheckIn]);

  /**
   * Handle modal no click (check-in as invalid)
   */
  const handleModalNoClick = useCallback(() => {
    performCheckIn("");
  }, [performCheckIn]);

  /**
   * Handle form confirmation (SH3 feedback form success — final step).
   * Also resets selectLatLong to WSS_PLACEHOLDER per production.
   */
  const handleFormConfirm = useCallback(() => {
    setGetSummeryList(selectedTerritory);
    showSuccess(`Successfully checked-in for ${wss ? wss : "this"} wss!`);
    setCheckinPayLoad({});
    setIsQuaterlyFlag({});
    setSelectLatLong(WSS_PLACEHOLDER);
  }, [
    selectedTerritory,
    wss,
    setGetSummeryList,
    showSuccess,
    setCheckinPayLoad,
    setIsQuaterlyFlag,
    setSelectLatLong,
  ]);

  /**
   * Handle form error (SH3 feedback form or API error)
   */
  const handleFormError = useCallback(() => {
    setIsSubmitModalOpen(false);
    showError(ERROR_MESSAGES.ALREADY_CHECKED_IN);
    setCheckinPayLoad({});
    setIsQuaterlyFlag({});
  }, [showError, setCheckinPayLoad, setIsSubmitModalOpen, setIsQuaterlyFlag]);

  // ===== NEW 2-step audit functions =====

  /**
   * HO Auditor / SH5: After audit popup, update existing record with audit data.
   */
  const proceedHOAuditorStampIn = useCallback(
    async (popupData) => {
      if (!recordId) {
        showError("Record ID not found. Please check in again.");
        return;
      }
      setLoading(true);

      const response = await myWssService.updateCheckIn({
        Record_ID: recordId,
        Retail_counter_present: popupData?.retailCounter || "",
        non_performing_status: popupData?.nonPerforming || "",
        Company_Name: popupData?.nonPerformingCompany || "",
      });

      setLoading(false);

      if (response.success) {
        setAuditPopupData({
          retailCounter: "",
          nonPerforming: "",
          nonPerformingCompany: "",
        });
        setGetSummeryList(selectedTerritory);
        setIsSubmitModalOpen(false);
        showSuccess(
          `Successfully checked-in for ${wss ? wss : "this"} wss!`
        );
      } else {
        showError(response.message || "Failed to update check-in");
      }
    },
    [
      recordId,
      wss,
      selectedTerritory,
      setLoading,
      setAuditPopupData,
      setGetSummeryList,
      setIsSubmitModalOpen,
      showSuccess,
      showError,
    ]
  );

  /**
   * SH3: After audit popup, store audit data and open feedback form.
   * The feedback form will do the work and then call handleSH3FeedbackFormSubmit.
   */
  const proceedSH3AuditAndFeedback = useCallback(
    (auditData) => {
      // Store audit data for later updateCheckIn call
      setAuditPopupData(auditData);

      // Build payload and set it — this triggers MyWssFeedbackForm to open
      const payload = prepareCheckInPayload("");
      // Override audit fields with the data from the popup
      payload.Retail_counter_present = auditData?.retailCounter || "";
      payload.non_performing_status = auditData?.nonPerforming || "";
      payload.Company_Name = auditData?.nonPerformingCompany || "";
      setCheckinPayLoad(payload);
    },
    [setAuditPopupData, prepareCheckInPayload, setCheckinPayLoad]
  );

  /**
   * SH3: After feedback form is submitted, update existing record with audit data.
   * Called from MyWssFeedbackForm's onUpdate prop.
   */
  const handleSH3FeedbackFormSubmit = useCallback(async () => {
    if (!recordId) {
      showError("Record ID not found. Please check in again.");
      return;
    }
    setLoading(true);

    const response = await myWssService.updateCheckIn({
      Record_ID: recordId,
      Retail_counter_present: auditPopupData?.retailCounter || "",
      non_performing_status: auditPopupData?.nonPerforming || "",
      Company_Name: auditPopupData?.nonPerformingCompany || "",
    });

    setLoading(false);

    if (response.success) {
      setAuditPopupData({
        retailCounter: "",
        nonPerforming: "",
        nonPerformingCompany: "",
      });
      setCheckinPayLoad({});
      setGetSummeryList(selectedTerritory);
      showSuccess(
        `Successfully checked-in for ${wss ? wss : "this"} wss!`
      );
    } else {
      showError(response.message || "Failed to update check-in");
    }
  }, [
    recordId,
    auditPopupData,
    wss,
    selectedTerritory,
    setLoading,
    setAuditPopupData,
    setCheckinPayLoad,
    setGetSummeryList,
    showSuccess,
    showError,
  ]);

  /**
   * Handle auto-populate from summary (SH3 only)
   * NOTE: The actual state updates (setSelectedTerritory, setSelectLatLong) are
   * done by MyWssForm.jsx directly. This hook only opens the modal.
   */
  return {
    handleCheckInClick,
    handleModalYesClick,
    handleModalNoClick,
    handleFormConfirm,
    handleFormError,
    handleNormalCheckin,
    handleDirectCheckIn,
    performCheckIn,
    prepareCheckInPayload,
    isValidCheckIn,
    // 2-step audit flow
    proceedHOAuditorStampIn,
    proceedSH3AuditAndFeedback,
    handleSH3FeedbackFormSubmit,
  };
};

export default useWssCheckIn;
